import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import Header from './Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Plus, 
  Search, 
  Filter, 
  ArrowLeft, 
  Shield, 
  Clock, 
  CheckCircle, 
  XCircle,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FileText,
  RefreshCw,
  Loader2
} from 'lucide-react';

const API = '/api';

const RiskRegister = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Audits and controls for selection
  const [audits, setAudits] = useState([]);
  const [controls, setControls] = useState([]);
  const [selectedAuditId, setSelectedAuditId] = useState('');
  
  // Workspace members for owner dropdown
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    audit_id: '',
    control_id: '',
    likelihood: 'possible',
    impact: 'moderate',
    status: 'open',
    monitoring_interval: 'monthly',
    owner: '',
    mitigation_plan: '',
    notes: ''
  });

  useEffect(() => {
    fetchRisks();
    fetchSummary();
    fetchAudits();
    fetchWorkspaceMembers();
  }, [currentWorkspace]);

  const fetchWorkspaceMembers = async () => {
    try {
      if (!currentWorkspace?.id) return;
      
      const response = await axios.get(`${API}/workspaces/${currentWorkspace.id}/members`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setWorkspaceMembers(response.data.members || response.data || []);
    } catch (error) {
      console.error('Error fetching workspace members:', error);
      // Fallback: try to get employees
      try {
        const empResponse = await axios.get(`${API}/employees`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const employees = Array.isArray(empResponse.data) ? empResponse.data : empResponse.data.employees || [];
        setWorkspaceMembers(employees.map(e => ({ 
          id: e.id, 
          full_name: e.full_name || e.name, 
          email: e.email 
        })));
      } catch (empError) {
        console.error('Error fetching employees:', empError);
      }
    }
  };

  const fetchRisks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (currentWorkspace?.id) {
        params.append('workspace_id', currentWorkspace.id);
      }
      
      const response = await axios.get(`${API}/risks?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setRisks(response.data.risks || []);
    } catch (error) {
      console.error('Error fetching risks:', error);
      toast.error('Failed to fetch risks');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (currentWorkspace?.id) {
        params.append('workspace_id', currentWorkspace.id);
      }
      
      const response = await axios.get(`${API}/risks/dashboard/summary?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      // The endpoint returns an array directly, not { audits: [...] }
      setAudits(Array.isArray(response.data) ? response.data : response.data.audits || []);
    } catch (error) {
      console.error('Error fetching audits:', error);
    }
  };

  const fetchControlsForAudit = async (auditId) => {
    try {
      const response = await axios.get(`${API}/audits/${auditId}/controls`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      // The endpoint returns an array directly, not { controls: [...] }
      setControls(Array.isArray(response.data) ? response.data : response.data.controls || []);
    } catch (error) {
      console.error('Error fetching controls:', error);
      setControls([]);
    }
  };

  const handleAuditChange = (auditId) => {
    setSelectedAuditId(auditId);
    setFormData(prev => ({ ...prev, audit_id: auditId, control_id: '' }));
    if (auditId) {
      fetchControlsForAudit(auditId);
    } else {
      setControls([]);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      audit_id: '',
      control_id: '',
      likelihood: 'possible',
      impact: 'moderate',
      status: 'open',
      monitoring_interval: 'monthly',
      owner: '',
      mitigation_plan: '',
      notes: ''
    });
    setSelectedAuditId('');
    setControls([]);
  };

  const handleCreateRisk = async () => {
    if (!formData.title || !formData.audit_id || !formData.control_id) {
      toast.error('Please fill in required fields (Title, Audit, Control)');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/risks`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Risk created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchRisks();
      fetchSummary();
    } catch (error) {
      console.error('Error creating risk:', error);
      toast.error(error.response?.data?.detail || 'Failed to create risk');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRisk = async () => {
    if (!selectedRisk) return;

    try {
      setSaving(true);
      await axios.put(`${API}/risks/${selectedRisk.id}`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Risk updated successfully');
      setShowEditDialog(false);
      setSelectedRisk(null);
      resetForm();
      fetchRisks();
      fetchSummary();
    } catch (error) {
      console.error('Error updating risk:', error);
      toast.error(error.response?.data?.detail || 'Failed to update risk');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRisk = async () => {
    if (!selectedRisk) return;

    try {
      setSaving(true);
      await axios.delete(`${API}/risks/${selectedRisk.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Risk deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedRisk(null);
      fetchRisks();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting risk:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete risk');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (risk) => {
    setSelectedRisk(risk);
    setFormData({
      title: risk.title || '',
      description: risk.description || '',
      audit_id: risk.audit_id || '',
      control_id: risk.control_id || '',
      likelihood: risk.likelihood || 'possible',
      impact: risk.impact || 'moderate',
      status: risk.status || 'open',
      monitoring_interval: risk.monitoring_interval || 'monthly',
      owner: risk.owner || '',
      mitigation_plan: risk.mitigation_plan || '',
      notes: risk.notes || ''
    });
    setSelectedAuditId(risk.audit_id || '');
    if (risk.audit_id) {
      fetchControlsForAudit(risk.audit_id);
    }
    setShowEditDialog(true);
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      critical: 'bg-rose-100 text-rose-800 border-rose-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return colors[level] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-rose-50 text-rose-700 border-rose-200',
      monitoring: 'bg-sky-50 text-sky-700 border-sky-200',
      mitigated: 'bg-amber-50 text-amber-700 border-amber-200',
      resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return colors[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="w-4 h-4" />,
      monitoring: <Eye className="w-4 h-4" />,
      mitigated: <Shield className="w-4 h-4" />,
      resolved: <CheckCircle className="w-4 h-4" />
    };
    return icons[status] || <AlertCircle className="w-4 h-4" />;
  };

  // Filter risks
  const filteredRisks = risks.filter(risk => {
    const matchesSearch = risk.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          risk.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          risk.audit_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          risk.control_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || risk.status === statusFilter;
    const matchesLevel = levelFilter === 'all' || risk.risk_level === levelFilter;
    return matchesSearch && matchesStatus && matchesLevel;
  });

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-appbg flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-appbg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Risk Register</h1>
              <p className="text-slate-600 mt-1">Manage and monitor risks across your audits and controls</p>
            </div>
            
            <Button
              onClick={() => {
                resetForm();
                setShowCreateDialog(true);
              }}
              className="bg-navy-600 hover:bg-navy-700"
              data-testid="create-risk-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Register New Risk
            </Button>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <Card className="bg-white border border-slate-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
                  <p className="text-xs text-slate-500">Total Risks</p>
                </CardContent>
              </Card>
              <Card className="bg-rose-50 border border-rose-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-rose-700">{summary.by_status?.open || 0}</p>
                  <p className="text-xs text-rose-600">Open</p>
                </CardContent>
              </Card>
              <Card className="bg-sky-50 border border-sky-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-sky-700">{summary.by_status?.monitoring || 0}</p>
                  <p className="text-xs text-sky-600">Monitoring</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border border-amber-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{summary.by_status?.mitigated || 0}</p>
                  <p className="text-xs text-amber-600">Mitigated</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border border-emerald-100">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{summary.by_status?.resolved || 0}</p>
                  <p className="text-xs text-emerald-600">Resolved</p>
                </CardContent>
              </Card>
              <Card className={`${summary.overdue_reviews > 0 ? 'bg-orange-50 border-orange-100' : 'bg-slate-50 border-slate-100'}`}>
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${summary.overdue_reviews > 0 ? 'text-orange-700' : 'text-slate-600'}`}>
                    {summary.overdue_reviews || 0}
                  </p>
                  <p className={`text-xs ${summary.overdue_reviews > 0 ? 'text-orange-600' : 'text-slate-500'}`}>Overdue Reviews</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Risk Level Summary */}
          {summary && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-rose-100 border border-rose-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-rose-800">{summary.by_level?.critical || 0}</p>
                    <p className="text-xs text-rose-600">Critical</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-rose-400" />
                </CardContent>
              </Card>
              <Card className="bg-orange-100 border border-orange-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-orange-800">{summary.by_level?.high || 0}</p>
                    <p className="text-xs text-orange-600">High</p>
                  </div>
                  <TrendingUp className="w-6 h-6 text-orange-400" />
                </CardContent>
              </Card>
              <Card className="bg-amber-100 border border-amber-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-amber-800">{summary.by_level?.medium || 0}</p>
                    <p className="text-xs text-amber-600">Medium</p>
                  </div>
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                </CardContent>
              </Card>
              <Card className="bg-emerald-100 border border-emerald-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-emerald-800">{summary.by_level?.low || 0}</p>
                    <p className="text-xs text-emerald-600">Low</p>
                  </div>
                  <Shield className="w-6 h-6 text-emerald-400" />
                </CardContent>
              </Card>
            </div>
          )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Search risks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="risk-search"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="mitigated">Mitigated</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="w-[150px]" data-testid="level-filter">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setLevelFilter('all'); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Risks Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Risks ({filteredRisks.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRisks.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No risks found</p>
                <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Register First Risk
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Risk ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Title</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Audit</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Control</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Level</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Owner</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRisks.map((risk) => (
                      <tr key={risk.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <span className="font-mono text-xs text-slate-500">{risk.id}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{risk.title}</p>
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">{risk.description}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-700">{risk.audit_name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-700 truncate max-w-[150px] block">{risk.control_name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getRiskLevelColor(risk.risk_level)}>
                            {risk.risk_level?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={getStatusColor(risk.status)}>
                            {getStatusIcon(risk.status)}
                            <span className="ml-1 capitalize">{risk.status}</span>
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-600">{risk.owner || '-'}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(risk)}
                              data-testid={`edit-risk-${risk.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedRisk(risk); setShowDeleteConfirm(true); }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-risk-${risk.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Risk Dialog */}
        <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setShowEditDialog(false);
            setSelectedRisk(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{showEditDialog ? 'Edit Risk' : 'Register New Risk'}</DialogTitle>
              <DialogDescription>
                {showEditDialog ? 'Update the risk details below' : 'Fill in the details to register a new risk'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Title */}
              <div>
                <label className="text-sm font-medium text-slate-700">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Risk title"
                  data-testid="risk-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the risk in detail"
                  rows={3}
                  data-testid="risk-description-input"
                />
              </div>

              {/* Audit Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700">Audit *</label>
                <Select value={selectedAuditId} onValueChange={handleAuditChange}>
                  <SelectTrigger data-testid="audit-select">
                    <SelectValue placeholder="Select an audit" />
                  </SelectTrigger>
                  <SelectContent>
                    {audits.map((audit) => (
                      <SelectItem key={audit.id} value={audit.id}>
                        {audit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Control Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700">Control *</label>
                <Select 
                  value={formData.control_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, control_id: v }))}
                  disabled={!selectedAuditId}
                >
                  <SelectTrigger data-testid="control-select">
                    <SelectValue placeholder={selectedAuditId ? "Select a control" : "Select an audit first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {controls.map((control) => (
                      <SelectItem key={control.id} value={control.id}>
                        {control.control_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Likelihood & Impact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Likelihood</label>
                  <Select value={formData.likelihood} onValueChange={(v) => setFormData(prev => ({ ...prev, likelihood: v }))}>
                    <SelectTrigger data-testid="likelihood-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rare">Rare</SelectItem>
                      <SelectItem value="unlikely">Unlikely</SelectItem>
                      <SelectItem value="possible">Possible</SelectItem>
                      <SelectItem value="likely">Likely</SelectItem>
                      <SelectItem value="almost_certain">Almost Certain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Impact</label>
                  <Select value={formData.impact} onValueChange={(v) => setFormData(prev => ({ ...prev, impact: v }))}>
                    <SelectTrigger data-testid="impact-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="insignificant">Insignificant</SelectItem>
                      <SelectItem value="minor">Minor</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="major">Major</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Status & Monitoring */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="monitoring">Monitoring</SelectItem>
                      <SelectItem value="mitigated">Mitigated</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Monitoring Interval</label>
                  <Select value={formData.monitoring_interval} onValueChange={(v) => setFormData(prev => ({ ...prev, monitoring_interval: v }))}>
                    <SelectTrigger data-testid="interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Owner */}
              <div>
                <label className="text-sm font-medium text-slate-700">Owner</label>
                <Select 
                  value={formData.owner || "unassigned"} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, owner: v === "unassigned" ? "" : v }))}
                >
                  <SelectTrigger data-testid="risk-owner-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">No owner assigned</SelectItem>
                    {workspaceMembers.map((member) => (
                      <SelectItem key={member.id || member.email} value={member.full_name || member.name || member.email}>
                        {member.full_name || member.name || member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mitigation Plan */}
              <div>
                <label className="text-sm font-medium text-slate-700">Mitigation Plan</label>
                <Textarea
                  value={formData.mitigation_plan}
                  onChange={(e) => setFormData(prev => ({ ...prev, mitigation_plan: e.target.value }))}
                  placeholder="Steps to mitigate or resolve this risk"
                  rows={3}
                  data-testid="mitigation-input"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes"
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
                setSelectedRisk(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button 
                onClick={showEditDialog ? handleUpdateRisk : handleCreateRisk}
                disabled={saving}
                className="bg-navy-600 hover:bg-navy-700"
                data-testid="save-risk-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : showEditDialog ? 'Update Risk' : 'Create Risk'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Risk</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this risk? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {selectedRisk && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-medium text-red-900">{selectedRisk.title}</p>
                  <p className="text-sm text-red-700 mt-1">{selectedRisk.id}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteRisk}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700"
              >
                {saving ? 'Deleting...' : 'Delete Risk'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

export default RiskRegister;
